// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgxEchartsDirective, provideEcharts, } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { DropdownComponent } from '../../dropdown/dropdown.component';
import { SvChrOpts, SvPieChartData } from '../../../interfaces/hce/hce-stats';

// Pie chart display data
type PieDatum = { name: string; value: number }[];

@Component({
  selector: 'app-hce-sv-pie-chart',
  standalone: true,
  imports: [
    NgxEchartsDirective,
    DropdownComponent,
    MatFormFieldModule,
    MatInputModule, // use echarts [options]="opt
  ],
  providers: [provideEcharts()], // Provide ECharts within this component
  templateUrl: './sv-pie-chart.component.html',
  styleUrls: ['./sv-pie-chart.component.css']
})
export class HceSvPieChartComponent  implements OnChanges {
  // Chr Dropdwon List
  @Input() chrOpts?: SvChrOpts = undefined;

  // Pie chart Data
  @Input() pieChartData?: SvPieChartData = undefined;

  // chart display size
  @Input() width: string = '220px';
  @Input() height: string = '380px'; // including the chart legend

  data: PieDatum = [];

  // char drop down
  dropdownOptions: Array<{ value: any; label: string }> = [];
  selectionValue: string = '0';
  selectPanelName = new FormControl(this.selectionValue, [Validators.required]);

  // dispay total
  total:string = '';

  // Pie chart options
  chartOption: any = {};
  mergeOption: EChartsOption = {};
  seriesColors: string[] = [];

  ngOnInit(): void {
    this.buildOptions();
  }

  private buildOptions(): void {
    this.chartOption = {
      animation: false,
      tooltip: {
        trigger: 'item',
        confine: true, // Prevent it from going off-screen
        formatter: (p: any) => `${p.name}<br/>${p.percent}%`
      },
      legend: {
        // Vertically stacked and positioned under the chart
        orient: 'vertical',
        top: '65%', // Align the starting position with the top boundary of the bottom section
        left: 'center',
        itemWidth: 12,
        itemHeight: 12,
        textStyle: {
          fontSize: 12
        },
        selectedMode: false, // Disable show/hide toggling on legend click
      },
      series: [
        {
          name: '',
          type: 'pie',
          radius: ['0%', '90%'], // Inner radius, outer radius
          center: ['50%', '30%'], // Shift the vertical position slightly upward to secure space for the legend
          legendHoverLink: true, // Keep the legend hover highlight active
          avoidLabelOverlap: true,
          stillShowZeroSum: true,
          label: {
            show: false
          },
          labelLine: {
            show: false
          },
          data: this.data,
          // Hover effect
          emphasis: {
            scale: false,
            scaleSize: 8,
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.3)'
            },
            label: {
              show: false, // Hide the numeric labels on the chart
              formatter: (p: any) => `${p.name}: ${p.value} (${p.percent}%)`
            }
          }
        }
      ],

      color: this.seriesColors,
    };
  }

  /**
   * Pie chart change event
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chrOpts']) {
      this.dropdownOptions = Object.entries(this.chrOpts ?? {}).map(([key, val]) => ({
        value: key,
        label: String(val),
      }));
      this.selectionValue = this.dropdownOptions?.[0]?.value ?? '';
      this.selectPanelName = new FormControl(this.selectionValue, [Validators.required]);
    }

    if (changes['pieChartData']) {
      this.handleChrChange(this.selectionValue);
    }

    this.buildOptions();
  }

  /**
   * Chr change event
   */
  handleChrChange(value: string) {
    this.selectionValue = value;
    if (this.pieChartData) {
      let opt = this.pieChartData[this.selectionValue];
      this.total = String(opt?.total ?? '');
      this.data = opt?.values.map(([name, value]) => ({ name, value })) ?? [];
      this.seriesColors = opt?.seriesColors;
    }

    this.buildOptions();
  }
}
